/**
 * ConditionFieldPicker — wybór pola warunku z wyszukiwaniem (F24).
 *
 * Lista pól potrafi mieć ponad setkę pozycji (rdzeń dokumentu + pola dodatkowe klasy + kontrahent
 * + obieg + tabele dodatkowe), a zwykła lista rozwijana zmusza wtedy do przewijania na ślepo.
 *
 * Stoi na prymitywie `MultiSelect`, bo ten ma już wszystko, czego tu trzeba: pole wyszukiwania,
 * panel renderowany przez portal (wewnątrz okna z przewijaniem własny panel zostałby przycięty)
 * i obsługę klawiatury. Wymuszamy na nim JEDEN wybór — warunek porównuje jedno pole.
 *
 * Nazwa grupy („Kontrahent", „Obieg akceptacji") ląduje w wierszu pod etykietą, bo bez niej lista
 * bywa myląca: „Status" znaczy co innego na dokumencie, a co innego u kontrahenta.
 *
 * Gdy ta sama nazwa pola występuje w kilku grupach (np. „Opiekun" faktury i „Opiekun" jej
 * kontrahenta), grupa trafia też do SAMEJ etykiety — „Opiekun (Kontrahent)". Wybrane pole pokazuje
 * się w polu wyboru bez podpisu, więc bez tego po wyborze nie widać, które pole ustawiono.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { MultiSelect, type MultiSelectOption } from '@/components/MultiSelect';
import type { ConditionFieldGroup } from '@/lib/use-condition-rules';

export interface ConditionFieldPickerProps {
  value: string;
  onChange: (path: string) => void;
  groups: ConditionFieldGroup[];
  disabled?: boolean;
}

export function ConditionFieldPicker({
  value,
  onChange,
  groups,
  disabled,
}: ConditionFieldPickerProps) {
  const { t } = useTranslation();

  const options = useMemo<MultiSelectOption[]>(() => {
    const labelCount = new Map<string, number>();
    for (const group of groups) {
      for (const field of group.fields) {
        labelCount.set(field.label, (labelCount.get(field.label) ?? 0) + 1);
      }
    }
    return groups.flatMap((group) =>
      group.fields.map((field) => ({
        value: field.path,
        label:
          (labelCount.get(field.label) ?? 0) > 1 ? `${field.label} (${group.label})` : field.label,
        description: group.label,
      })),
    );
  }, [groups]);

  return (
    <div className="min-w-56 flex-1">
      <MultiSelect
        value={value ? [value] : []}
        options={options}
        disabled={disabled}
        placeholder={t('tenant.permissions.conditions.pickField')}
        onChange={(next) => {
          // Wymuszenie jednego wyboru: bierzemy ostatnio kliknięty. Ponowne kliknięcie
          // zaznaczonej pozycji odznaczyłoby ją — wtedy zostawiamy dotychczasowe pole,
          // bo warunek bez pola nie ma sensu i nie dałby się zapisać.
          const picked = next.find((path) => path !== value) ?? next[0] ?? value;
          if (picked) onChange(picked);
        }}
      />
    </div>
  );
}
